import React from 'react';
import styled from 'styled-components';
import { Quote, User, Award, Shield } from 'lucide-react';
import mediatorPhoto from '../assets/MED1.jpeg';

const PageContainer = styled.div`
  padding-top: 90px;
  min-height: 100vh;
  background: var(--background-light);
`;

const Hero = styled.section`
  background: linear-gradient(135deg, var(--primary-blue) 0%, #004d8f 100%);
  padding: 4rem 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  }
`;

const HeroContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-family: var(--font-secondary);
  font-size: 3rem;
  color: var(--white);
  margin-bottom: 1rem;
  font-weight: 800;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  max-width: 700px;
  margin: 0 auto;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem 4rem;
`;

const ContentWrapper = styled.div`
  margin-top: -3rem;
  position: relative;
  z-index: 2;
`;

const MainCard = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--border-light);
  overflow: hidden;
`;

const MessageSection = styled.div`
  padding: 3rem;

  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const QuoteIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, var(--accent-gold), #ffd940);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 2rem;
  color: var(--primary-blue-dark);
`;

const WelcomeText = styled.p`
  font-size: 1.15rem;
  color: var(--text-dark);
  line-height: 1.8;
  text-align: center;
  font-weight: 500;
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 2px solid var(--border-light);
`;

const MessageParagraph = styled.p`
  font-size: 1.05rem;
  color: var(--text-medium);
  line-height: 2;
  margin-bottom: 1.5rem;
  text-align: justify;
`;

const SignatureSection = styled.div`
  background: linear-gradient(135deg, rgba(0, 38, 100, 0.05), rgba(254, 202, 0, 0.05));
  padding: 3rem;
  text-align: center;
  border-top: 1px solid var(--border-light);

  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const MediatorPhoto = styled.div`
  width: 120px;
  height: 120px;
  background: linear-gradient(135deg, var(--primary-blue), #004d8f);
  border-radius: 50%;
  margin: 0 auto 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 4px solid var(--accent-gold);
  color: var(--white);
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center top;
  }
`;

const RoleText = styled.p`
  font-size: 0.95rem;
  color: var(--text-medium);
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const NameText = styled.h2`
  font-family: var(--font-secondary);
  font-size: 1.75rem;
  color: var(--primary-blue);
  font-weight: 700;
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-top: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ValueCard = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  padding: 2rem;
  box-shadow: var(--shadow-md);
  border: 1px solid var(--border-light);
  text-align: center;
  transition: var(--transition);

  &:hover {
    transform: translateY(-8px);
    box-shadow: var(--shadow-xl);
    border-color: var(--accent-gold);
  }

  svg {
    color: var(--accent-gold);
    margin-bottom: 1rem;
  }

  h3 {
    font-family: var(--font-secondary);
    font-size: 1.1rem;
    color: var(--text-dark);
    margin-bottom: 0.5rem;
  }

  p {
    font-size: 0.9rem;
    color: var(--text-medium);
    line-height: 1.6;
  }
`;

const MotDuMediateur = () => {
  return (
    (<PageContainer>
      <Hero>
        <HeroContent>
          <HeroTitle>Mot du Médiateur de la République</HeroTitle>
          <HeroSubtitle>
            Un message du Médiateur de la République à tous les citoyens tchadiens
          </HeroSubtitle>
        </HeroContent>
      </Hero>
      <Container>
        <ContentWrapper>
          <MainCard>
            <MessageSection>
              <QuoteIcon>
                <Quote size={40} />
              </QuoteIcon>

              <WelcomeText>
                Bienvenue sur le site officiel de la Médiature de la République du Tchad.
              </WelcomeText>

              <MessageParagraph>
                La Médiature de la République est une institution indépendante, au service des citoyens, qui œuvre pour la promotion de l’État de droit, le renforcement de la bonne gouvernance et le règlement pacifique des différends entre l’administration et les administrés.
              </MessageParagraph>

              <MessageParagraph>
                Ce site internet, désormais pleinement opérationnel, se veut avant tout un outil de proximité, d’accessibilité et de transparence. Il permet à chaque citoyenne et à chaque citoyen, où qu’il se trouve, de déposer des réclamations sans avoir à se déplacer, de suivre l’évolution de leur dossier et de mieux comprendre les missions, les actions ainsi que les mécanismes de recours offerts par la Médiature de la République.
              </MessageParagraph>

              <MessageParagraph>
                À travers cette plateforme, nous souhaitons également encourager les citoyens à alerter l’institution sur toute situation d’injustice, de dysfonctionnement administratif ou d’atteinte aux droits, afin de contribuer activement à l’amélioration du service public et au renforcement de la confiance entre l’administration et les administrés.
              </MessageParagraph>

              <MessageParagraph>
                Je vous remercie pour l’intérêt porté à la Médiature de la République et vous assure de notre engagement constant à œuvrer pour une administration plus juste, plus équitable et résolument tournée vers les citoyens.
              </MessageParagraph>
            </MessageSection>

            <SignatureSection>
              <MediatorPhoto>
                <img src={mediatorPhoto} alt="Amb. Saleh KEBZABO" />
              </MediatorPhoto>
              <RoleText>Le Médiateur de la République</RoleText>
              <NameText>Amb. Saleh KEBZABO</NameText>
            </SignatureSection>
          </MainCard>

          <ValuesGrid>
            <ValueCard>
              <Shield size={40} />
              <h3>Indépendance</h3>
              <p>Une institution indépendante au service des citoyens</p>
            </ValueCard>
            <ValueCard>
              <Award size={40} />
              <h3>Équité</h3>
              <p>Pour une administration plus juste et équitable</p>
            </ValueCard>
            <ValueCard>
              <User size={40} />
              <h3>Proximité</h3>
              <p>À l'écoute des citoyens partout au Tchad</p>
            </ValueCard>
          </ValuesGrid>
        </ContentWrapper>
      </Container>
    </PageContainer>)
  );
};

export default MotDuMediateur;

